import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CampDetails from '../components/camps/CampDetails';
import { useGetOrganizer } from '../api/organizers';
import { useAnalytics } from '../context/AnalyticsContext';
import { Camp } from '../utils/types';
import { Calendar, Clock, Users, DollarSign, ExternalLink } from 'lucide-react';
import './Organizer.css';
import { CampFormatUtils } from '../utils/campFormatUtils';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

const CALENDLY_URL = 'https://calendly.com/jenni-aika/30min';

const Organizer: React.FC = () => {
  const { organizerId } = useParams<{ organizerId: string }>();
  const { trackPageView } = useAnalytics();
  const { data: organizer, isLoading, error } = useGetOrganizer(organizerId);
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [showCampDetails, setShowCampDetails] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (organizerId) {
      trackPageView(`/organizer/${organizerId}`, 'Organizer');
    }
  }, [organizerId, trackPageView]);

  useEffect(() => {
    const cssId = 'calendly-widget-css';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      document.head.appendChild(link);
    }

    const scriptId = 'calendly-widget-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleScheduleCall = () => {
    window.Calendly?.initPopupWidget({ url: CALENDLY_URL });
  };


  const getCampImage = (camp: Camp): string => {
    return camp.images.find(image => image.type === 'default')?.url || '/images/camp-card-default-image.png';
  };

  const handleCampClick = (camp: Camp) => {
    setSelectedCamp(camp);
    setShowCampDetails(true);
  };

  const handleCloseCampDetails = () => {
    setShowCampDetails(false);
    setSelectedCamp(null);
  };

  if (isLoading) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading organizer information...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="error-container">
            <h1>Organizer Not Found</h1>
            <p>Sorry, we couldn't find the organizer you're looking for.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!organizer) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="error-container">
            <h1>No Data Available</h1>
            <p>No organizer data is available at this time.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        <div className="organizer-page-content">
          {/* Left Column */}
          <div className="organizer-left-column">
            {/* Organizer Information Section */}
            <section className="organizer-info">
              <div className="organizer-header">
                <h1 className="organizer-name">{organizer.name}</h1>
                {organizer.short_description && (
                  <p className="organizer-tagline">{organizer.short_description}</p>
                )}
              </div>

              <div className="organizer-details">
                <div className="organizer-info-grid">
                  {organizer.description && (
                    <div className="info-item">
                      <strong>About:</strong>
                      <span>{organizer.description}</span>
                    </div>
                  )}

                  {organizer.email && (
                    <div className="info-item">
                      <strong>Email:</strong>
                      <a href={`mailto:${organizer.email}`}>{organizer.email}</a>
                    </div>
                  )}

                  {organizer.phone && (
                    <div className="info-item">
                      <strong>Phone:</strong>
                      <a href={`tel:${organizer.phone}`}>{organizer.phone}</a>
                    </div>
                  )}

                  {organizer.website && (
                    <div className="info-item">
                      <strong>Website:</strong>
                      <a href={organizer.website} target="_blank" rel="noopener noreferrer">
                        {organizer.website}
                      </a>
                    </div>
                  )}

                  {organizer.street_address && (
                    <div className="info-item">
                      <strong>Address:</strong>
                      <span>
                        {organizer.street_address}
                        {organizer.city && `, ${organizer.city}`}
                        {organizer.state && `, ${organizer.state}`}
                        {organizer.zip_code && ` ${organizer.zip_code}`}
                      </span>
                    </div>
                  )}

                  {organizer.google_rating && (
                    <div className="info-item">
                      <strong>Rating:</strong>
                      <div className="rating-inline">
                        <span className="rating-stars">
                          {'★'.repeat(Math.floor(organizer.google_rating))}
                          {'☆'.repeat(5 - Math.floor(organizer.google_rating))}
                        </span>
                        <span className="rating-value">{organizer.google_rating.toFixed(1)}</span>
                        {organizer.google_rating_count && (
                          <span className="rating-count">({organizer.google_rating_count} reviews)</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Camps Section */}
            <section className="organizer-camps">
              <div className="camps-header">
                <h3>
                  {organizer.camps.length} camp{organizer.camps.length !== 1 ? 's' : ''} available
                </h3>
              </div>

              <div className="organizer-camps-list">
                {organizer.camps.map((camp) => {
                  const priceParts = CampFormatUtils.getPriceParts(camp);
                  return (
                    <div key={camp.id} className="organizer-camp-row">
                      <div className="organizer-camp-image-container">
                        <img
                          src={getCampImage(camp)}
                          alt={camp.name}
                          className="organizer-camp-image"
                        />
                      </div>

                      <div className="organizer-camp-details">
                        <h3 className="organizer-camp-name">{camp.name}</h3>

                        {camp.description && (
                          <p className="organizer-camp-description">
                            {camp.description.length > 150
                              ? `${camp.description.substring(0, 150).replace(/\s+\S*$/, '')}...`
                              : camp.description}
                          </p>
                        )}

                        <div className="organizer-camp-info">
                          <div className="organizer-info-item">
                            <Clock className="icon icon--sm" />
                            <span>{CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
                          </div>
                          <div className="organizer-info-item">
                            <Users className="icon icon--sm" />
                            <span>{CampFormatUtils.formatAgeRange(camp.min_age, camp.max_age)}</span>
                          </div>
                        </div>

                        {camp.camp_dates.length > 0 && (
                          <div className="organizer-camp-dates-list">
                            {camp.camp_dates
                              .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
                              .map((campDate) => (
                                <div key={campDate.id} className="organizer-camp-date-row">
                                  <Calendar className="icon icon--xs" />
                                  <span>{CampFormatUtils.formatDateRange(campDate.start_date, campDate.end_date, false)}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                      <div className="organizer-camp-actions">
                        <div className="organizer-camp-price">
                          <DollarSign className="icon icon--sm" />
                          <span className="price-amount">${priceParts.price}</span>
                          {priceParts.timeframe && (
                            <span className="price-timeframe">/ {priceParts.timeframe}</span>
                          )}
                        </div>
                        <button
                          className="btn btn--primary btn--sm"
                          onClick={() => handleCampClick(camp)}
                        >
                          <ExternalLink className="icon icon--xs" />
                          Show details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {/* Right Panel */}
          <div className="organizer-right-panel">
            <div className="organizer-cta-card">
              <h2 className="organizer-cta-title">Grow your camp enrollment</h2>
              <p className="organizer-cta-body">
                 "Built by a local mom of three who knows the summer scramble, Aika connects Colorado families with camps like yours.
                We offer marketing visibility and optional direct booking support.
              </p>
              <p className="organizer-cta-body">
                Curious what that could look like for you?
              </p>
              <button className="btn btn--primary organizer-cta-btn" onClick={handleScheduleCall}>
                Schedule a Call
              </button>
              <p className="organizer-cta-footnote">No pressure. Just a conversation about your enrollment goals.</p>

              <div className="organizer-cta-quotes">
                <blockquote className="organizer-cta-quote">
                  "We've so far had at least five kids sign up through your service! Thank you!"
                  <cite>— Camp director, STEM camp in Boulder</cite>
                </blockquote>
                <blockquote className="organizer-cta-quote">
                  "I can't believe it's already time to think about summer camps! 🤦‍♀️ This site is a godsend 🙏 Thanks for sharing."
                  <cite>— Local Parent on Facebook</cite>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Camp Details Modal */}
      {showCampDetails && selectedCamp && (
        <CampDetails
          camp={selectedCamp}
          isOpen={showCampDetails}
          onClose={handleCloseCampDetails}
        />
      )}
    </div>
  );
};

export default Organizer;
